const docCookies = require("mozilla-doc-cookies");

module.exports = class DrupalOAuth {
	constructor(args) {
		if (args && args.baseUrl) {
			this.baseUrl = args.baseUrl;
		} else {
			this.baseUrl = this.getBaseUrl();
		}
		if (args && args.clientId) {
			this.clientId = this.clientId;
		} else {
			this.clientId = this.getClientId();
		}

		// Initialize token state
		this.token = false;
		this.tokenExpires = 0;
		this.refreshInProgress = false;
		this.tokenRefreshBuffer = 15 * 1000; // Refresh token 60 seconds before expiration
		this.tokenCheckInterval = null;

		// Load tokens from cookies if available
		if (
			typeof window !== `undefined` &&
			docCookies.hasItem("refresh_token") &&
			docCookies.hasItem("access_token")
		) {
			this.token = {
				refresh_token: docCookies.getItem("refresh_token"),
				access_token: docCookies.getItem("access_token"),
			};

			// Get token expiration time if available
			if (docCookies.hasItem("token_expires")) {
				this.tokenExpires = parseInt(docCookies.getItem("token_expires"), 10);
			}
		}
		this.isLoggedIn = this.isLoggedIn.bind(this);
		this.checkAndRefreshToken = this.checkAndRefreshToken.bind(this);
	}

	getBaseUrl() {
		if (!process.env.CLF_BACKEND_URL && !this.baseUrl) {
			throw new Error(
				"Missing base url for Entity Sync. Please set the CLF_BACKEND_URL environment variable or pass in `baseUrl` to the DrupalOAuth object as the base url of your backend, like 'https://www.my-backend.com'"
			);
		}
		if (this.baseUrl) {
			return this.baseUrl;
		}
		return process.env.CLF_BACKEND_URL.replace(/\/$/, ""); // strip end slash
	}

	getClientId() {
		if (!process.env.DRUPAL_CONSUMER_ID && !this.clientId) {
			throw new Error(
				"Missing client ID for Entity Sync. Please set the DRUPAL_CONSUMER_ID environment variable or pass in `clientId` to the DrupalOAuth object as the OAuth client_id of your app."
			);
		}
		if (this.clientId) {
			return this.clientId;
		}
		return process.env.DRUPAL_CONSUMER_ID;
	}

	/**
	 * Check if token is about to expire and refresh it proactively
	 * @returns {Promise<boolean>} True if token was refreshed, false otherwise
	 */
	async checkAndRefreshToken() {
		// Don't do anything if we're not logged in or a refresh is already in progress
		if (!this.token) {
			return false;
		}
		if (this.refreshInProgress) {
			return null;
		}
		const now = Date.now();
		// If token is about to expire or we don't know when it expires, refresh it
		if (!this.tokenExpires || now + this.tokenRefreshBuffer > this.tokenExpires) {
			try {
				this.refreshInProgress = true;
				const newToken = await this.refresh();
				this.refreshInProgress = false;
				return !!newToken;
			} catch (error) {
				console.error("Error refreshing token:", error);
				this.refreshInProgress = false;
				return false;
			}
		}
		return false;
	}

	/**
	 * Validates if the current token is still valid by checking expiration
	 * @returns {boolean} True if token is valid, false otherwise
	 */
	isTokenValid() {
		if (!this.token) {
			return false;
		}

		// If we don't know when the token expires, assume it's invalid
		if (!this.tokenExpires) {
			return false;
		}

		return Date.now() < this.tokenExpires;
	}

	getInit(method, headers, body, token) {
		const init = {
			method: method,
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/vnd.api+json",
			},
		};
		Object.assign(init.headers, headers);
		if (body) {
			init.body = body;
		}
		Object.assign(init.headers, headers);
		return init;
	}

	/**
	 *
	 * @param resp
	 * @returns {Promise<object | boolean>} returns a truthy value if this is a valid response
	 */
	async verifyResponse(resp, returnResponse, url, method, body, headers, second = false) {
		try {
			if (!resp.ok && second) {
				if (returnResponse) {
					return resp;
				} else {
					let respo_body = await resp.json();
					let respo_msg = "";
					if (respo_body) {
						respo_msg = respo_body.message ? respo_body.message : respo_body.error;
					}
					return {
						status: resp.status,
						error: `${resp.status} ${resp.statusText}: ${respo_msg}`,
					};
				}
			}
			// check match for 2XX status for success
			if (/2../.test(resp.status.toString())) {
				if (returnResponse) {
					return resp;
				} else {
					return await resp.json();
				}
			} else if (
				/4../.test(resp.status.toString()) ||
				/5../.test(resp.status.toString())
			) {
				const newToken = await this.refresh();
				if (newToken) {
					// Token refresh successful, retrying request
					const init = this.getInit(method, headers, body, newToken.access_token);

					const secondResp = await fetch(url, init);
					const validSecondResponse = await this.verifyResponse(
						secondResp,
						returnResponse,
						url,
						method,
						body,
						headers,
						true
					);
					return validSecondResponse;
				} else {
					// Token refresh failed, logging out
					this.removeTokens();
					this.token = false;
					if (returnResponse) {
						return resp;
					} else {
						throw new Error("Token refresh failed, logging out");
					}
				}
			}
			if (!returnResponse) {
				throw new Error("Error verifyResponse");
			}
		} catch (error) {
			console.error("Error at verifyResponse ", error);
			return { error: error.message };
		}
		return returnResponse ? resp : { error: "General Error verifyResponse" };
	}

	async drupalFetch(
		jsonapiEndpoint,
		method = "GET",
		body = null,
		headers = null,
		extraParams = {},
		dispatch = null
	) {
		if (!this.isLoggedIn()) {
			return { error: "User not logged in" };
		}

		const { useDefaultJsonApiPath = true, returnResponse = false } = extraParams;
		if (body && !(body instanceof File)) {
			body = JSON.stringify(body);
		}
		const base = this.getBaseUrl();

		const url = useDefaultJsonApiPath
			? `${base}/jsonapi/${jsonapiEndpoint}`
			: `${base}/${jsonapiEndpoint}`;

		const init = this.getInit(method, headers, body, this.token.access_token);
		try {
			const resp = await fetch(url, init);

			const validResponse = await this.verifyResponse(
				resp,
				returnResponse,
				url,
				method,
				body,
				headers
			);

			if (returnResponse) {
				return validResponse;
			} else {
				if (validResponse.error) {
					const unauthorized_request =
						validResponse.status && /4../.test(validResponse.status.toString());
					if(unauthorized_request){
						console.error("4** Unauthorized attempts");
					}
					// if (unauthorized_request && dispatch) {
					// 	console.error("Logging out due to 4** Unauthorized attempts");
					// 	this.removeTokens();
					// 	localStorage.clear();
					// 	this.token = false;
					// 	this.tokenExpires = 0;
					// 	dispatch({
					// 		type: "LOGOUT_USER",
					// 		payload: {
					// 			isAuthenticated: false,
					// 			clientId: null,
					// 			baseUrl: null,
					// 		},
					// 	});
					// }
					return { ...validResponse };
				} else {
					return validResponse;
				}
			}
		} catch (error) {
			console.log("Error in drupalFetch:", error);

			return {
				error,
			};
		}
	}

	/**
	 * Uses the refresh token stored in the cookie to get a new oauth token.
	 * @returns {Promise<object | Boolean>} A token object with `refresh_token` and `access_token`, or false if no
	 * existing refresh token or login failed (meaning this user is not logged in)
	 */
	async refresh() {
		let refreshToken = false;
		if (typeof window !== `undefined`) {
			refreshToken = docCookies.getItem("refresh_token");
		}
		if (typeof refreshToken !== `undefined` && refreshToken) {
			return await this.authPost("refresh_token", refreshToken);
		}

		return false;
	}

	/**
	 * Checks if the user is logged in and has a valid token
	 * @returns {boolean}
	 */
	isLoggedIn() {
		// is logged in?
		let this_token = this.token ? true : false;
		if (this_token) {
			if (
				typeof window !== `undefined` &&
				(!docCookies.hasItem("refresh_token") || !docCookies.hasItem("access_token"))
			) {
				return false;
			} else {
				return true;
			}
		}
		return false;
	}

	/**
	 * Authenticates against a Drupal oauth backend. Will set or unset cookie with refresh token on success or failure.
	 *
	 * @param grantType Either `'password'` or `'refresh_token'`
	 * @param authValue The value of the refresh token, if using `refresh_token` grant type
	 * @param FormData object, if you are using the password grant, you should turn your form element into a formData object then pass to authPost. This way you can manipulate the formObject outside of the authPost method.
	 * @returns {Promise<object | Boolean>} A token object with `refresh_token` and `access_token`, or false if login
	 * failed
	 */
	async authPost(grantType, authValue = null, form = null) {
		const base = this.getBaseUrl();

		const url = base + "/oauth/token";

		const formData = form ? form : new FormData();

		if (authValue) {
			formData.append(grantType, authValue);
		}
		formData.append("grant_type", grantType);
		formData.append("client_id", this.getClientId());

		const init = {
			method: "POST",
			body: formData,
		};
		let resp;
		try {
			resp = await fetch(url, init);
		} catch (error) {
			console.error("Error fetching authentication response:", error);
		}
		if (resp && resp.status) {
			if (/2../.test(resp.status.toString())) {
				try {
					this.token = await resp.json();
					// this.token

					// Calculate token expiration time (default to 1 hour if expires_in not provided)
					const expiresIn = this.token.expires_in || 3600;
					this.tokenExpires = Date.now() + expiresIn * 1000;

					if (typeof window !== `undefined`) {
						// For inherit sessions to iframes, for handling authorization through Drupal OAuth
						// Set reasonable cookie expiration (30 days)
						const cookieExpiration = 60 * 60 * 24 * 30;

						docCookies.setItem(
							"refresh_token",
							this.token.refresh_token,
							cookieExpiration,
							"/; SameSite=None; Secure"
						);
						docCookies.setItem(
							"access_token",
							this.token.access_token,
							cookieExpiration,
							"/; SameSite=None; Secure"
						);
						docCookies.setItem(
							"token_expires",
							this.tokenExpires.toString(),
							cookieExpiration,
							"/; SameSite=None; Secure"
						);
					}

					return this.token;
				} catch (error) {
					console.error("Error parsing authentication response:", error);
				}
			} else {
				try {
					const errorData = await resp.json();
					console.error("Authentication failed:", errorData);
				} catch (e) {
					console.error("Authentication failed with status:", resp.status);
				}
			}
		}
		// If we get here, authentication failed
		this.removeTokens();
		this.token = false;
		this.tokenExpires = 0;
		return false;
	}

	/**
	 *
	 * @param formData
	 * @param grantType
	 * @returns {Promise<{token: Object|Boolean, uid: null}>}
	 */
	async loginUser(formData, grantType) {
		const initialLoginResponse = await this.authPost(grantType, null, formData);
		// initialLoginResponse
		return { token: initialLoginResponse };
	}

	async logoutUser() {
		// >>> logoutUser

		if (typeof window !== `undefined`) {
			this.removeTokens();
			// Removing all localStorage for page
			localStorage.clear();
			// Removing current access tokens/response
			this.token = false;
			this.tokenExpires = 0;

			return !docCookies.hasItem("refresh_token");
		} else {
			return true;
		}
	}

	removeTokens() {
		if (typeof window !== `undefined`) {
			docCookies.removeItem("refresh_token", "/");
			docCookies.removeItem("access_token", "/");
			docCookies.removeItem("token_expires", "/");
		}
	}
};
